/**
 * Customer API
 *
 * API endpoints for customer management operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { Customer, CustomerPagination, EditCustomerPayload, ListCustomer } from "./customer.types";
import { editCustomerArgsSchema } from "./schemas";

/**
 * Customer API endpoints
 * Provides CRUD operations and search functionality for customer management
 */
export const customerApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all customers
     * @returns Array of customers
     */
    getCustomers: build.query<Customer[], void>({
      query: () => "/customer",
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando clientes...",
          success: "Clientes carregados com sucesso!",
          error: "Erro ao carregar os clientes."
        });
      }
    }),

    /**
     * Get a single customer by ID
     * @param id - Customer ID
     * @returns Full customer details
     */
    getCustomer: build.query<Customer, Pick<Customer, "id">>({
      query: ({ id }) => `/customer/${id}`,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando cliente...",
          success: "Cliente carregado com sucesso!",
          error: "Erro ao carregar o cliente."
        });
      }
    }),

    /**
     * Create or update a customer
     * @param payload - Customer data (id = 0 or undefined for create, id > 0 for update)
     * @returns Updated customer data
     */
    editCustomer: build.mutation<EditCustomerPayload, EditCustomerPayload>({
      query: ({ id = 0, ...body }) => ({
        url: "/customer/create",
        method: "POST",
        body: {
          id,
          ...body
        }
      }),
      onQueryStarted: (arg, { queryFulfilled }) => {
        const { id } = arg;
        toast.promise(queryFulfilled, {
          loading: id ? "Editando cliente..." : "Criando cliente...",
          success: id ? "Cliente editado com sucesso!" : "Cliente criado com sucesso!",
          error: id ? "Erro ao editar o cliente." : "Erro ao criar o cliente."
        });
      },
      argSchema: editCustomerArgsSchema
    }),

    /**
     * Delete a customer
     * @param id - Customer ID to delete
     * @returns void
     */
    deleteCustomer: build.mutation<void, Pick<Customer, "id">>({
      query: ({ id }) => ({
        url: `/customer/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo cliente...",
          success: "Cliente excluído com sucesso!",
          error: "Erro ao excluir o cliente."
        });
      }
    }),

    /**
     * List customers with pagination (infinite query)
     * @param params - Filter and pagination parameters
     * @returns Paginated list of customers
     */
    listCustomers: build.infiniteQuery<ListCustomer, { params?: CustomerPagination }, Omit<CustomerPagination, "filter"> & { companyId?: number }, ListCustomer>({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 50, sortDirection: "asc", sortField: "name" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: "/customer",
        params: Object.assign({}, pageParam, params)
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando clientes...",
          success: "Clientes carregados com sucesso!",
          error: "Erro ao carregar os clientes."
        });
      }
    }),

    /**
     * Search customers by query string
     * @param query - Search query string
     * @returns Array of matching customers
     */
    searchCustomers: build.query<Customer[], { query: string }>({
      query: ({ query }) => ({
        url: `/customer/like/${query}`
      })
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
customerApi.enhanceEndpoints({
  addTagTypes: ["Customer", "ListCustomer"],
  endpoints: {
    getCustomers: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Customer" as const, id })), { type: "Customer", id: "LIST" }] : [{ type: "Customer", id: "LIST" }])
    },
    getCustomer: {
      providesTags: (result, error, { id }) => [{ type: "Customer", id }]
    },
    editCustomer: {
      invalidatesTags: (result, error, { id }) =>
        !error ? [id ? { type: "Customer", id } : { type: "Customer", id: "LIST" }, { type: "Customer", id: "LIST" }, { type: "ListCustomer", id: "LIST" }] : []
    },
    listCustomers: {
      providesTags: (result) =>
        result
          ? [...result.pages.flatMap(({ data }) => data.map(({ id }) => ({ type: "ListCustomer" as const, id }))), { type: "ListCustomer", id: "LIST" }]
          : [{ type: "ListCustomer", id: "LIST" }]
    },
    deleteCustomer: {
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: "Customer", id },
              { type: "Customer", id: "LIST" },
              { type: "ListCustomer", id: "LIST" }
            ]
          : []
    },
    searchCustomers: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Customer" as const, id })), { type: "Customer", id: "LIST" }] : [{ type: "Customer", id: "LIST" }])
    }
  }
});
